// The viewport: moving the stage's camera with the pointer.
//
// Drag to orbit around the camera's target, drag with the right button (or with
// Shift held, or with two fingers) to pan, and turn the wheel (or pinch) to move
// closer or further away. A double-click goes back to the model's fixed camera.
// While a gesture runs the scene shows each step at once (preview); when it ends
// the camera goes into the view state (commit), so the address and a shared link
// hold it (docs/contracts/view-state.md, field `camera`). The fixed cameras stay
// in the camera bar, which a keyboard reaches.
//
// A press that moves less than DRAG_PX is a click, which the stage uses to pick
// a part; a drag swallows the click that follows it.
//
// The camera maths is in plain functions (orbit, pan, dolly) for the tests.

import type { Camera, Vec3 } from "../state/view-state";

export interface ViewportOptions {
  /** The element that takes the gestures: the stage's canvas. */
  element: HTMLElement;
  /** The camera shown now, or null before a model has loaded. */
  current(): Camera | null;
  /** Shows a camera at once, during a gesture. */
  preview(camera: Camera): void;
  /** Puts a camera into the view state, at the end of a gesture. */
  commit(camera: Camera): void;
  /** Goes back to the model's fixed camera (a double-click). */
  reset(): void;
}

/** A press that moves less than this (in CSS pixels) is a click. */
export const DRAG_PX = 4;
/** Orbit radians per CSS pixel dragged. */
export const ORBIT_PER_PX = 0.008;
/** The camera stays this far (radians) from straight up or down, where its "up" would flip. */
export const POLE_MARGIN = 0.05;
/** Nearest and furthest distance from the target, in metres. */
export const MIN_DISTANCE = 0.05;
export const MAX_DISTANCE = 20;
/** Distance factor per wheel unit (a notch of 100 moves about 11 %). */
const WHEEL_SCALE = 0.0011;
/** A wheel gesture commits after this long without a turn, in ms. */
const WHEEL_COMMIT_MS = 250;
/** Committed cameras are rounded to 0.1 mm, so addresses stay short. */
const ROUND = 1e4;

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];
const length = (a: Vec3): number => Math.hypot(a[0], a[1], a[2]);
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const normalize = (a: Vec3): Vec3 => scale(a, 1 / (length(a) || 1));

/** Turns the camera around its target: `azimuth` about the vertical, `polar` towards it (radians, positive is down). */
export function orbit(camera: Camera, azimuth: number, polar: number): Camera {
  const offset = sub(camera.position, camera.target);
  const r = length(offset);
  const p0 = Math.acos(Math.min(1, Math.max(-1, offset[1] / (r || 1))));
  // Straight up or down the azimuth is undefined: take the one the scene shows there, front at the top.
  const a0 = Math.hypot(offset[0], offset[2]) < 1e-12 ? Math.atan2(0, offset[1] > 0 ? -1 : 1) : Math.atan2(offset[0], offset[2]);
  const p = Math.min(Math.PI - POLE_MARGIN, Math.max(POLE_MARGIN, p0 + polar));
  const a = a0 + azimuth;
  const position: Vec3 = [
    camera.target[0] + r * Math.sin(p) * Math.sin(a),
    camera.target[1] + r * Math.cos(p),
    camera.target[2] + r * Math.sin(p) * Math.cos(a),
  ];
  return { position, target: [...camera.target], fov: camera.fov };
}

/**
 * Slides the camera and its target across the picture by (dx, dy) CSS pixels of
 * a picture `height` pixels high, so the point under the pointer stays under it.
 */
export function pan(camera: Camera, dx: number, dy: number, height: number): Camera {
  const offset = sub(camera.position, camera.target);
  const dir = normalize(scale(offset, -1));
  // As the scene does (engine-scene.ts): looking straight down, the picture's up is +Z.
  const worldUp: Vec3 = Math.abs(dir[1]) > 0.999 ? [0, 0, 1] : [0, 1, 0];
  const right = normalize(cross(dir, worldUp));
  const up = cross(right, dir);
  const perPx = (2 * length(offset) * Math.tan((camera.fov * Math.PI) / 360)) / Math.max(1, height);
  const move = add(scale(right, -dx * perPx), scale(up, dy * perPx));
  return { position: add(camera.position, move), target: add(camera.target, move), fov: camera.fov };
}

/** Moves the camera towards (factor < 1) or away from (factor > 1) its target. */
export function dolly(camera: Camera, factor: number): Camera {
  const offset = sub(camera.position, camera.target);
  const r = length(offset);
  const next = Math.min(MAX_DISTANCE, Math.max(MIN_DISTANCE, r * factor));
  return { position: add(camera.target, scale(offset, next / (r || 1))), target: [...camera.target], fov: camera.fov };
}

/** A camera rounded for the address. */
export function rounded(camera: Camera): Camera {
  const r = (v: number): number => Math.round(v * ROUND) / ROUND + 0; // + 0 turns −0 into 0
  return {
    position: [r(camera.position[0]), r(camera.position[1]), r(camera.position[2])],
    target: [r(camera.target[0]), r(camera.target[1]), r(camera.target[2])],
    fov: Math.round(camera.fov * 100) / 100,
  };
}

type Gesture = "orbit" | "pan";

export class Viewport {
  private readonly options: ViewportOptions;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private start: { x: number; y: number } | null = null;
  private gesture: Gesture = "orbit";
  private dragging = false;
  private suppressClick = false;
  private camera: Camera | null = null;
  private wheelTimer = 0;
  private readonly offs: (() => void)[] = [];

  constructor(options: ViewportOptions) {
    this.options = options;
    const el = options.element;
    const on = <K extends keyof HTMLElementEventMap>(
      type: K,
      handler: (e: HTMLElementEventMap[K]) => void,
      opts?: AddEventListenerOptions,
    ): void => {
      el.addEventListener(type, handler, opts);
      this.offs.push(() => el.removeEventListener(type, handler, opts));
    };
    on("pointerdown", (e) => this.down(e));
    on("pointermove", (e) => this.move(e));
    on("pointerup", (e) => this.up(e));
    on("pointercancel", (e) => this.up(e));
    on("wheel", (e) => this.wheel(e), { passive: false });
    on("contextmenu", (e) => e.preventDefault());
    on("dblclick", (e) => {
      e.preventDefault();
      this.options.reset();
    });
    // In the capture phase, so the stage's pick never sees the click that ends a drag.
    on(
      "click",
      (e) => {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        e.stopImmediatePropagation();
        e.preventDefault();
      },
      { capture: true },
    );
    el.style.touchAction = "none";
  }

  /** True while a drag is under way. */
  get active(): boolean {
    return this.dragging;
  }

  dispose(): void {
    clearTimeout(this.wheelTimer);
    for (const off of this.offs.splice(0)) off();
  }

  private down(e: PointerEvent): void {
    if (e.pointerType === "mouse" && e.button !== 0 && e.button !== 1 && e.button !== 2) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.options.element.setPointerCapture?.(e.pointerId);
    if (this.pointers.size === 1) {
      this.start = { x: e.clientX, y: e.clientY };
      this.gesture = e.button === 2 || e.button === 1 || e.shiftKey ? "pan" : "orbit";
      this.dragging = false;
      this.suppressClick = false;
      this.camera = this.options.current();
    } else {
      // A second finger: pan and pinch from here on.
      this.gesture = "pan";
      this.dragging = true;
    }
  }

  private move(e: PointerEvent): void {
    const last = this.pointers.get(e.pointerId);
    if (!last || this.start === null) return;
    const dx = e.clientX - last.x;
    const dy = e.clientY - last.y;
    if (!this.dragging && Math.hypot(e.clientX - this.start.x, e.clientY - this.start.y) < DRAG_PX) return;
    const before = this.spread();
    last.x = e.clientX;
    last.y = e.clientY;
    this.dragging = true;
    let camera = this.camera ?? this.options.current();
    if (camera === null) return;
    const n = this.pointers.size;
    if (n >= 2) {
      // Each finger moves the middle by 1/n of its own step; the spread pinches.
      camera = pan(camera, dx / n, dy / n, this.options.element.clientHeight);
      const after = this.spread();
      if (before > 0 && after > 0) camera = dolly(camera, before / after);
    } else if (this.gesture === "pan") {
      camera = pan(camera, dx, dy, this.options.element.clientHeight);
    } else {
      camera = orbit(camera, -dx * ORBIT_PER_PX, -dy * ORBIT_PER_PX);
    }
    this.camera = camera;
    this.options.preview(camera);
  }

  private up(e: PointerEvent): void {
    if (!this.pointers.delete(e.pointerId)) return;
    if (this.pointers.size > 0) return;
    if (this.dragging && this.camera !== null) {
      this.suppressClick = true;
      this.options.commit(rounded(this.camera));
    }
    this.dragging = false;
    this.start = null;
    this.camera = null;
  }

  private wheel(e: WheelEvent): void {
    const base = this.camera ?? this.options.current();
    if (base === null) return;
    e.preventDefault();
    const units = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
    const camera = dolly(base, Math.exp(units * WHEEL_SCALE));
    this.camera = camera;
    this.options.preview(camera);
    clearTimeout(this.wheelTimer);
    this.wheelTimer = window.setTimeout(() => {
      if (this.camera !== null && this.pointers.size === 0) this.options.commit(rounded(this.camera));
      if (this.pointers.size === 0) this.camera = null;
    }, WHEEL_COMMIT_MS);
  }

  /** The mean distance of the pointers from their middle, or 0 with fewer than two. */
  private spread(): number {
    if (this.pointers.size < 2) return 0;
    let x = 0;
    let y = 0;
    for (const p of this.pointers.values()) {
      x += p.x;
      y += p.y;
    }
    x /= this.pointers.size;
    y /= this.pointers.size;
    let d = 0;
    for (const p of this.pointers.values()) d += Math.hypot(p.x - x, p.y - y);
    return d / this.pointers.size;
  }
}
